import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Recipe } from '../../schemas/recipe';
import { FULL_RECIPES } from '../../data/fullRecipes';
import { FullRecipeBody } from './FullRecipeBody';

interface RecipeModalProps {
  recipe: Recipe | null;
  onClose: () => void;
}

/**
 * Popup that shows a recipe in detail. Triggered by clicking a recipe card.
 * Closes on overlay click, the X button, or Escape.
 *
 * Rendered through a React Portal at document.body level so it escapes
 * any ancestor with a CSS transform (.fade-in sections use translateY,
 * which would otherwise break position: fixed and push the modal off-
 * viewport when scrolled).
 *
 * Body scroll is locked while the modal is open. The modal-body has its
 * own internal scroll so long recipe content stays accessible without
 * moving the modal itself.
 */
export function RecipeModal({ recipe, onClose }: RecipeModalProps) {
  // Lock body scroll while open.
  useEffect(() => {
    if (!recipe) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [recipe]);

  // Close on Escape.
  useEffect(() => {
    if (!recipe) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [recipe, onClose]);

  if (!recipe) return null;

  // A card may or may not have a real recipe behind it. When it does, the
  // modal is the recipe; when it doesn't, it stays the teaser it always was.
  const full = FULL_RECIPES[recipe.id];

  const modal = (
    <div
      className="recipe-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Recipe: ${recipe.name}`}
    >
      <div
        className="recipe-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="recipe-modal-close"
          onClick={onClose}
          aria-label="Close recipe"
        >
          ×
        </button>

        {recipe.photo && (
          <div className="recipe-modal-photo">
            <img
              src={`${process.env.PUBLIC_URL}${recipe.photo}`}
              alt={recipe.name}
            />
          </div>
        )}

        <div className="recipe-modal-body">
          <div className="recipe-modal-chapter">{recipe.chapter}</div>
          <h2 className="recipe-modal-title">{recipe.name}</h2>
          <p className="recipe-modal-desc">{recipe.description}</p>

          <div className="recipe-modal-tags">
            {recipe.tags.map((t) => (
              <span key={t} className="recipe-meta-tag">{t}</span>
            ))}
          </div>

          {full ? (
            <FullRecipeBody recipe={full} />
          ) : (
            <div className="recipe-modal-coming-soon">
              <div className="section-label">Full recipe</div>
              <p>
                Step-by-step instructions, ingredients, and ratios coming soon. For now: <a href="mailto:jarvis@truenooch.com?subject=Recipe%20request">email us</a> and we’ll send you the kitchen notes directly.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
